import type { WebSockets } from '@pushflow/types'
import { EAction } from '@pushflow/types/poker/enums'
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAnal,
  useAuthWebSocket,
  useModal,
  useShareUrl,
  useTableSound,
} from '../../../hooks'
import {
  incommingChatMessageAtom,
  userSettingsAtom,
} from '../../../store/atoms'
import type { ERound } from './../../../types/enums'
import {
  ECurrency,
  EGameType,
  EModalName,
  ETableSound,
} from '../../../types/enums'
import { trpc } from '../../../utils'
import { PlayerControls, TableMenuBar, TablePlayer } from '../../organisms'
import { PokerRoom } from '../../templates'
import { useHandleIncomingMessages, useMultiTable } from './hooks'
import * as styles from './index.css'

export const TableView = (props: {
  userId: string
  currentTableId: string
  currentPassword: string | null
  tables: {
    id: string
    password: string | null
  }[]
  onChangeTable: (tableId: string) => void
  onJoinNewTable: (table: { id: string; password: string | null }) => void
  onCloseTable: (tableId: string) => void
}) => {
  const userSettings = useAtomValue(userSettingsAtom)
  const { play: playSound } = useTableSound({
    volume: userSettings.volume / 100,
  })
  const {
    multiTableProps,
    gameData,
    userPlayer,
    lastMessage,
    standUpFromTable,
    onHandleQueueMessage,
  } = useMultiTable({
    userId: props.userId,
    currentTableId: props.currentTableId,
    tables: props.tables,
    onJoinNewTable: props.onJoinNewTable,
    onTableChange: props.onChangeTable,
    onUsersTurn: async () => {
      await playSound(ETableSound.USERS_TURN)
    },
  })
  const { data: priveleges } = trpc.table.getPriveleges.useQuery({
    tableId: props.currentTableId,
  })
  const { t } = useTranslation()
  const getInvite = trpc.table.getInvite.useMutation()

  const sendReserve = trpc.player.sendReserve.useMutation()
  const sendMove = trpc.player.sendMove.useMutation()

  const { closeModal } = useModal()
  const { logEvent } = useAnal()
  const { shareUrl } = useShareUrl()

  const [tonConnectUi] = useTonConnectUI()

  const userTonAddress = useTonAddress()
  const setIncommingChatMessage = useSetAtom(incommingChatMessageAtom)

  const {
    isAuthorizied: isChatAuthorized,
    lastJsonMessage: chatMessage,
    sendJsonMessage: sendChatMessage,
  } = useAuthWebSocket<WebSockets.ChatTableWebSocketOutputType>(
    userSettings.showGameChat ? `chat/${props.currentTableId}` : null,
  )
  const [chatTextMessage, setChatTextMessage] = useState<{
    userId: string
    text: string
  }>()

  const handleReserve = (seat?: number) => {
    sendReserve.mutate({
      tableId: props.currentTableId,
      seat,
    })
  }

  useHandleIncomingMessages({
    userId: props.userId,
    incomingMessage: lastMessage,
    onHandleQueueMessage,
    onCloseTable: () => props.onCloseTable(props.currentTableId),
  })

  useEffect(() => {
    if (!isChatAuthorized) {
      return
    }

    const chatConnectMessage = {
      type: 'connect',
    }
    sendChatMessage(chatConnectMessage)
  }, [isChatAuthorized, sendChatMessage])

  useEffect(() => {
    if (chatMessage?.type === 'chat') {
      // TODO: get rid of the atom. Pop up the chat or pass as a prop
      setIncommingChatMessage(chatMessage.data)
      setChatTextMessage({
        userId: chatMessage.data.userId,
        text: chatMessage.data.text,
      })
    }
  }, [chatMessage])

  useEffect(() => {
    return () => {
      closeModal({
        name: EModalName.TABLE_BUY_IN,
      })
    }
  }, [])

  const leaveTable = () => {
    const didStood = standUpFromTable()
    if (!didStood) {
      return
    }
    props.onCloseTable(props.currentTableId)
  }

  const onClickDeleteTable = () => {
    if (!gameData) {
      return
    }
    if (gameData?.table?.round === 'DEAL') {
      return
    }
  }

  const onClickMove = (action: EAction, amount?: number) => {
    if (!userPlayer) {
      return
    }
    const finalAction =
      amount && userPlayer.buyIn <= amount ? EAction.ALL_IN : action
    const data =
      amount && (finalAction === EAction.RAISE || finalAction === EAction.BET)
        ? {
            action: finalAction,
            amount,
          }
        : {
            action: finalAction as Exclude<
              EAction,
              EAction.RAISE | EAction.BET
            >,
          }
    sendMove.mutate({
      tableId: props.currentTableId,
      data,
    })
  }

  const onSuggestDoubleBoard = () => {}

  const onNewTableCards = async (count: number) => {
    for (let index = 0; index < count; index++) {
      await playSound(ETableSound.CARD_DROP)
    }
  }

  const onClickPlayerPlug = async (action: 'sit' | 'invite', seat: number) => {
    if (!gameData) {
      return
    }
    if (action === 'sit') {
      const isSystemCryptoCurrency =
        gameData.info.currency === ECurrency.TON ||
        gameData.info.currency === ECurrency.USDT
      if (isSystemCryptoCurrency && !userTonAddress) {
        await tonConnectUi.openModal()
        if (!userTonAddress) {
          return
        }
      }
      handleReserve(seat)
      return
    }
    if (action === 'invite') {
      const invite = await getInvite.mutateAsync({
        tableId: props.currentTableId,
        password: props.currentPassword,
      })
      logEvent('table_shared_invite_link')
      await shareUrl({
        link: invite.url,
        text: t('table.label.invite'),
      })
    }
  }

  const controlProps = useMemo(() => {
    if (!userPlayer || !gameData) return { showButtons: false } as const

    const isDoubleBoardPossible =
      gameData.info.isDoubleBoardPossible &&
      !['RIVER', 'SHOWDOWN'].includes(gameData.table.round) &&
      gameData.players.filter(
        (player) => player.cards && player.move !== EAction.FOLD,
      ).length === 2

    const isOmaha =
      gameData.info.gameType === EGameType.PLO4 ||
      gameData.info.gameType === EGameType.PLO5
    const callAmount = gameData.table.minBet - userPlayer.bet
    const sharedProps = {
      showButtons: true as const,
      playerBet: userPlayer.bet,
      bigBlind: gameData.info.bigBlind,
      pot: isOmaha
        ? gameData.table.maxBet
        : gameData.table.totalBet + callAmount,
      minBet: gameData.table.minBet,
      maxBet: gameData.table.maxBet
        ? Math.min(gameData.table.maxBet, userPlayer.buyIn)
        : userPlayer.buyIn,
      minRaise:
        (gameData.table.minRaise || gameData.info.bigBlind) + callAmount,
      buyIn: userPlayer.buyIn,
      isDoubleBoardPossible,
      possibleActions: gameData.table.possibleActions as EAction[],
    }

    const isPlayerTurn = gameData.table.playerTurnId === props.userId
    const canAct =
      userPlayer.move !== EAction.FOLD &&
      gameData.table.possibleActions.length > 0

    if (isPlayerTurn && canAct) {
      return sharedProps
    }

    if (
      gameData.table.round === 'DEAL' &&
      userPlayer.status === 'AWAIT_BB' &&
      userPlayer.buyIn >= gameData.info.minBuyIn &&
      gameData.players.filter((player) => player.status === 'IDLE').length >= 2
    ) {
      return {
        ...sharedProps,
        minBet: gameData.info.bigBlind,
        maxBet: gameData.info.bigBlind,
        possibleActions: [EAction.BET],
      }
    }

    return { showButtons: false } as const
  }, [userPlayer, gameData, props.currentTableId])

  return (
    <div className={styles.tablePage}>
      <TableMenuBar
        tableId={props.currentTableId}
        userId={props.userId}
        currency={gameData?.info.currency}
        isUserSit={userPlayer !== null}
        isPaused={gameData?.table?.isPaused || false}
        isAwaitingDeletion={gameData?.table?.isAwaitingDeletion || false}
        showAdminSettings={
          gameData?.info?.type === 'PRIVATE' && priveleges?.host === true
        }
        playersCount={gameData?.players?.length || 0}
        multiTableProps={multiTableProps}
        onClickDelete={onClickDeleteTable}
        onClickLeave={leaveTable}
      />
      {gameData ? (
        <>
          <div className={styles.pokerRoom}>
            <PokerRoom
              userId={props.userId}
              valuesInBigBlinds={userSettings.valuesInBigBlinds}
              table={gameData.table}
              tableInfo={gameData.info}
              players={gameData.players}
              userSeat={userPlayer?.seat ?? null}
              pots={gameData.pots}
              winners={gameData.winners}
              modes={{
                doubleBoard: gameData.info.isDoubleBoardPossible
                  ? gameData.table.isDoubleBoardActive
                    ? 'active'
                    : 'inactive'
                  : null,
                bombPot:
                  gameData.info.bombPotMin &&
                  gameData.info.bombPotMax &&
                  gameData.info.bombPotFrequency &&
                  gameData.info.bombPotActivateDoubleBoard !== null
                    ? {
                        min: gameData.info.bombPotMin,
                        max: gameData.info.bombPotMax,
                        frequency: gameData.info.bombPotFrequency,
                        activateDoubleBoard:
                          gameData.info.bombPotActivateDoubleBoard,
                        state:
                          gameData.info.bombPotFrequency <= 1 ||
                          gameData.table.lastBombPotTriggeredHandNumber ===
                            null ||
                          gameData.table.handNumber ===
                            gameData.table.lastBombPotTriggeredHandNumber
                            ? 'active'
                            : 'inactive',
                      }
                    : null,
              }}
              chatMessage={chatTextMessage}
              renderUserPlayer={!controlProps.showButtons}
              onClickMove={onClickMove}
              onClickPlayerPlug={onClickPlayerPlug}
              onPlayerMove={(userId, move) =>
                void playSound(move as Extract<ETableSound, EAction>)
              }
              onNewTableCards={(count) => {
                void onNewTableCards(count)
              }}
              onPlayerWin={(userId) => {
                if (userId === props.userId) {
                  void playSound(ETableSound.WIN)
                }
              }}
            />
          </div>

          {controlProps.showButtons ? (
            <PlayerControls
              {...controlProps}
              tableId={props.currentTableId}
              currency={gameData.info.currency}
              isDoubleBoardActive={gameData.table.isDoubleBoardActive}
              onClickMove={onClickMove}
              onSuggestDoubleBoard={onSuggestDoubleBoard}
            >
              {userPlayer ? (
                <TablePlayer
                  className={styles.userPlayer}
                  userId={userPlayer.userId}
                  name={userPlayer.name}
                  avatar={userPlayer.avatar}
                  cards={userPlayer.cards}
                  bet={userPlayer.bet}
                  buyIn={userPlayer.buyIn}
                  bigBlind={gameData.info.bigBlind}
                  move={userPlayer.move}
                  seat={userPlayer.seat}
                  faceDirection={'right'}
                  round={gameData.table.round as ERound}
                  handNumber={gameData.table.handNumber}
                  turnSeconds={gameData.info.turnSeconds}
                  turnTimeout={
                    gameData.table.playerTurnId === props.userId &&
                    gameData.table.turnStartedAt
                      ? gameData.info.turnSeconds -
                        Math.floor(
                          (Date.now() -
                            new Date(gameData.table.turnStartedAt).getTime()) /
                            1000,
                        )
                      : null
                  }
                  communityCards={gameData.table.cards}
                  status={userPlayer.status}
                  isSnapshot={true}
                  isOnline={userPlayer.isOnline}
                  isLeaving={userPlayer.isLeaving}
                  isCurrentTurnPlayer={
                    gameData.table.playerTurnId === props.userId
                  }
                  isWinner={
                    gameData.winners.length === 0
                      ? undefined
                      : !!gameData.winners.find(
                          (winner) => winner.id === props.userId,
                        )
                  }
                  onMove={() => null}
                />
              ) : null}
            </PlayerControls>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
